import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Grid,
  Divider,
  TextField,
  Alert,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { getAvailableTournaments, getLastRankingFecha, createRankingDraft, updateRankingDraft } from '../../services/ranking.service';

const parseDateToYYYYMMDD = (val) => {
  if (!val) return '';
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return '';
    return val.toISOString().split('T')[0];
  }
  const str = String(val).trim();
  const ymdMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymdMatch) {
    return ymdMatch[1];
  }
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    return d.toISOString().split('T')[0];
  }
  return '';
};

const formatFecha = (dateStr) => {
  const ymd = parseDateToYYYYMMDD(dateStr);
  if (!ymd) return '';
  const [year, month, day] = ymd.split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
};

/**
 * Panel izquierdo/derecho del Transfer List.
 * Muestra una lista de torneos con checkboxes.
 */
function TransferPanel({ title, items, checked, onToggle, readOnly = false }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 350,
        maxHeight: 450,
      }}
    >
      <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="caption" sx={{ color: '#60a5fa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
          {title} ({items.length})
        </Typography>
      </Box>
      <List dense sx={{ overflowY: 'auto', flex: 1, py: 0 }}>
        {items.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#475569' }}>Sin torneos</Typography>
          </Box>
        )}
        {items.map((tor) => (
          <ListItemButton
            key={tor.id}
            dense
            onClick={readOnly ? undefined : () => onToggle(tor.id)}
            disableRipple={readOnly}
            sx={{
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              cursor: readOnly ? 'default' : undefined,
              '&:hover': readOnly ? {} : { background: 'rgba(58,110,182,0.1)' },
            }}
          >
            {!readOnly && (
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  size="small"
                  checked={checked.includes(tor.id)}
                  tabIndex={-1}
                  disableRipple
                  sx={{ color: '#475569', '&.Mui-checked': { color: '#3a6eb6' } }}
                />
              </ListItemIcon>
            )}
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <span style={{ color: '#64748b', marginRight: '6px' }}>
                    {formatFecha(tor.fechaInicio)} -
                  </span>
                  {tor.nombre}
                </Typography>
              }
            />
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );
}

export default function StepTorneos({
  readOnly = false,
  isEdit,
  rkgId,
  fecha,
  setFecha,
  torneosAsignados,
  setTorneosAsignados,
  onSaveSuccess,
  onNext,
  onSalir,
  setAlertInfo,
}) {
  const [available, setAvailable] = useState([]);         // panel izquierdo
  const [assigned, setAssigned] = useState(torneosAsignados); // panel derecho
  const [leftChecked, setLeftChecked] = useState([]);
  const [rightChecked, setRightChecked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [minFecha, setMinFecha] = useState('');
  const [validationError, setValidationError] = useState('');
  const savedRef = useRef(false);

  // carga de torneos disponibles y última fecha
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        console.log('[StepTorneos] Cargando torneos disponibles y última fecha de ranking...');
        const [avail, lastFechaRes] = await Promise.all([
          getAvailableTournaments(isEdit ? rkgId : undefined),
          getLastRankingFecha(),
        ]);

        console.log('[StepTorneos] Torneos disponibles recibidos:', avail);
        console.log('[StepTorneos] Última fecha de ranking recibida:', lastFechaRes);

        // En modo edición: los asignados ya están en `assigned`.
        // Separamos los disponibles que NO estén en asignados.
        const assignedIds = new Set((torneosAsignados || []).map((t) => t.id));
        const availList = Array.isArray(avail) ? avail : [];
        setAvailable(availList.filter((t) => !assignedIds.has(t.id)));
        setAssigned([...(torneosAsignados || [])]);

        if (lastFechaRes?.lastFecha) {
          // minFecha = día siguiente al último ranking
          const lastYmd = parseDateToYYYYMMDD(lastFechaRes.lastFecha);
          if (lastYmd) {
            const [year, month, day] = lastYmd.split('-').map(Number);
            const d = new Date(Date.UTC(year, month - 1, day));
            d.setUTCDate(d.getUTCDate() + 1);
            setMinFecha(d.toISOString().split('T')[0]);
          }
        }
      } catch (err) {
        console.error('[StepTorneos] Error cargando torneos disponibles:', err);
        setAlertInfo({
          open: true,
          message: `Error cargando torneos disponibles: ${err?.message || err}`,
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLeft = (id) =>
    setLeftChecked((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const toggleRight = (id) =>
    setRightChecked((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  /** Mover seleccionados de izquierda → derecha */
  const moveToRight = () => {
    const toMove = available.filter((t) => leftChecked.includes(t.id));
    setAssigned((prev) => [...prev, ...toMove]);
    setAvailable((prev) => prev.filter((t) => !leftChecked.includes(t.id)));
    setLeftChecked([]);
  };

  /** Mover seleccionados de derecha → izquierda */
  const moveToLeft = () => {
    const toMove = assigned.filter((t) => rightChecked.includes(t.id));
    setAvailable((prev) => [...prev, ...toMove]);
    setAssigned((prev) => prev.filter((t) => !rightChecked.includes(t.id)));
    setRightChecked([]);
  };

  const validate = useCallback(() => {
    if (!fecha) {
      setValidationError('Debe ingresar una fecha.');
      return false;
    }
    if (minFecha && fecha < minFecha) {
      setValidationError(`La fecha debe ser igual o posterior al ${formatFecha(minFecha)}.`);
      return false;
    }
    setValidationError('');
    return true;
  }, [fecha, minFecha]);

  const doSave = useCallback(async () => {
    if (!validate()) return false;
    setSaving(true);
    const torIds = assigned.map((t) => t.id);
    try {
      if (!rkgId) {
        const res = await createRankingDraft(fecha, torIds);
        onSaveSuccess(res.rkg_id);
      } else {
        await updateRankingDraft(rkgId, fecha, torIds);
      }
      setTorneosAsignados(assigned);
      savedRef.current = true;
      setAlertInfo({ open: true, message: 'Ranking guardado correctamente.', severity: 'success' });
      return true;
    } catch (e) {
      setAlertInfo({ open: true, message: e.message || 'Error al guardar.', severity: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  }, [fecha, assigned, rkgId, validate, onSaveSuccess, setTorneosAsignados, setAlertInfo]);

  const handleGuardar = async () => {
    await doSave();
  };

  const handleSiguiente = async () => {
    const ok = await doSave();
    if (ok) onNext();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Fecha */}
      <Box sx={{ mb: 3, maxWidth: 260 }}>
        {readOnly ? (
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
              Fecha del ranking
            </Typography>
            <Typography variant="body1" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
              {formatFecha(fecha) || '—'}
            </Typography>
          </Box>
        ) : (
          <>
            <TextField
              label="Fecha del ranking"
              type="date"
              value={fecha}
              onChange={(e) => { setFecha(e.target.value); setValidationError(''); savedRef.current = false; }}
              inputProps={{ min: minFecha || undefined }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root': { color: '#cbd5e1' },
                '& .MuiInputLabel-root': { color: '#94a3b8' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3a6eb6' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3a6eb6' },
              }}
            />
            {validationError && (
              <Alert severity="error" sx={{ mt: 1, borderRadius: 0, py: 0.5 }}>
                {validationError}
              </Alert>
            )}
            {minFecha && (
              <Typography variant="caption" sx={{ color: '#475569', mt: 0.5, display: 'block' }}>
                Mínimo: {formatFecha(minFecha)}
              </Typography>
            )}
          </>
        )}
      </Box>

      {readOnly ? (
        /* En modo solo lectura: solo panel de torneos asignados */
        <>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1.5 }}>
            Torneos incluidos en este cálculo de ranking.
          </Typography>
          <Box sx={{ maxWidth: 600 }}>
            <TransferPanel
              readOnly
              title="Torneos incluidos"
              items={assigned}
              checked={[]}
              onToggle={() => {}}
            />
          </Box>
        </>
      ) : (
        /* En modo edición: transfer list completo */
        <>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1.5 }}>
            Seleccioná los torneos a incluir en este cálculo de ranking.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch' }}>
            {/* Panel izquierdo */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TransferPanel
                title="Torneos disponibles"
                items={available}
                checked={leftChecked}
                onToggle={toggleLeft}
              />
            </Box>

            {/* Flechas */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, alignItems: 'center', justifyContent: 'center', gap: 1, px: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={moveToRight}
                disabled={leftChecked.length === 0}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  borderColor: '#3a6eb6',
                  color: '#3a6eb6',
                  '&:hover': { borderColor: '#2563eb', color: '#2563eb' },
                  '&.Mui-disabled': { borderColor: '#334155', color: '#334155' },
                }}
              >
                <ArrowForwardIcon fontSize="small" />
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={moveToLeft}
                disabled={rightChecked.length === 0}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  borderColor: '#3a6eb6',
                  color: '#3a6eb6',
                  '&:hover': { borderColor: '#2563eb', color: '#2563eb' },
                  '&.Mui-disabled': { borderColor: '#334155', color: '#334155' },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </Button>
            </Box>

            {/* Panel derecho */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TransferPanel
                title="Torneos seleccionados"
                items={assigned}
                checked={rightChecked}
                onToggle={toggleRight}
              />
            </Box>
          </Box>
        </>
      )}

      <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.08)' }} />

      {/* Botones de acción */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {readOnly ? (
          /* Solo lectura: solo botón Siguiente sin guardar */
          <Button
            variant="contained"
            endIcon={<NavigateNextIcon />}
            onClick={onNext}
            sx={{
              bgcolor: '#3a6eb6',
              '&:hover': { bgcolor: '#2563eb' },
            }}
          >
            Siguiente
          </Button>
        ) : (
          /* Modo edición: Salir + Guardar + Siguiente */
          <>
            <Button
              variant="text"
              startIcon={<ExitToAppIcon />}
              onClick={onSalir}
              sx={{ color: '#64748b', '&:hover': { color: '#94a3b8' } }}
            >
              Salir
            </Button>
            <Button
              variant="outlined"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleGuardar}
              disabled={saving}
              sx={{
                borderColor: '#475569',
                color: '#94a3b8',
                '&:hover': { borderColor: '#94a3b8', color: '#cbd5e1' },
              }}
            >
              Guardar
            </Button>
            <Button
              variant="contained"
              endIcon={saving ? <CircularProgress size={16} /> : <NavigateNextIcon />}
              onClick={handleSiguiente}
              disabled={saving}
              sx={{
                bgcolor: '#3a6eb6',
                '&:hover': { bgcolor: '#2563eb' },
              }}
            >
              Siguiente
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
